// Actions across scans: one action is an op (or several) per scan, all with the same time and message,
// so history shows one entry and undo takes them back together.
import type { LogSession, OpBody, Staged } from './session.ts';

/** One action as committed: an op per scan, to undo/redo together. */
export type Action = { scan: string; id: string; parent: string | null }[];

/** A shared stamp: commits with the same time and message are one action. */
export interface Stamp { t: string; msg: string }

export const stampNow = (msg: string): Stamp => ({ t: new Date().toISOString(), msg });

/**
 * Commit each job's ops in turn (scan by scan), all with one stamp. What got committed is pushed onto
 * done as it goes, so a caller still has it when a later commit fails (a 409: another page moved HEAD).
 */
export async function commitMany(jobs: { s: LogSession; bodies: OpBody[] }[], label: string, stamp: Stamp, done: Action = []): Promise<Action> {
  for (const j of jobs) for (const b of j.bodies) {
    const parent = j.s.head;
    const id = await j.s.commit({ ...b, msg: stamp.msg } as OpBody, label, stamp.t);
    done.push({ scan: j.s.scan, id, parent });
  }
  return done;
}

/** Undo the newest action: every session whose HEAD op has the newest time (and message) steps back. Returns what it undid. */
export async function undoNewest(sessions: Iterable<LogSession>): Promise<Action | null> {
  const all = [...sessions];
  let best: { t: string; msg?: string } | null = null;
  const headOp = (s: LogSession) => s.graph.ops.find(o => o.id === s.head);
  for (const s of all) { const o = headOp(s); if (o && (!best || o.t > best.t)) best = o; }
  if (!best) return null;
  const act: Action = [];
  for (const s of all) {
    // an action may be several ops in a scan (same time and message): undo them all
    for (let o = headOp(s); o && o.t === best.t && o.msg === best.msg; o = headOp(s)) {
      await s.move(o.parent, 'undo');
      act.push({ scan: s.scan, id: o.id, parent: o.parent });
    }
  }
  return act;
}

/** Redo an undone action, where each scan's HEAD is still where the undo left it. */
export async function redoAction(byScan: (scan: string) => LogSession | undefined, act: Action) {
  for (const a of [...act].reverse()) { const s = byScan(a.scan); if (s && s.head === a.parent) await s.move(a.id, 'redo'); }
}

/** Stage each job's ops (applied in the browser only, LogSession.stage), all with one stamp. Returns how many per scan. */
export function stageMany(jobs: { s: LogSession; bodies: OpBody[] }[], label: string, stamp: Stamp): Map<string, number> {
  const n = new Map<string, number>();
  try {
    for (const j of jobs) for (const b of j.bodies) {
      j.s.stage({ ...b, msg: stamp.msg } as OpBody, label, stamp.t);
      n.set(j.s.scan, (n.get(j.s.scan) ?? 0) + 1);
    }
  } catch (e) {
    for (const j of jobs) j.s.unstage(n.get(j.s.scan) ?? 0);   // all or nothing
    throw e;
  }
  return n;
}

/** Write every session's staged ops to its log (one HEAD move per scan). */
export async function saveAll(sessions: Iterable<LogSession>) {
  for (const s of sessions) if (s.dirty) await s.flush();
}

/** A staged action taken back: its ops per scan, to stage again on redo. */
export type StagedAction = { scan: string; ops: Staged[] }[];

/** Take back the newest staged action (every session's newest staged ops with its time and message). */
export function unstageNewest(sessions: Iterable<LogSession>): StagedAction | null {
  const all = [...sessions], last = (s: LogSession) => s.pending[s.pending.length - 1];
  let best: Staged | null = null;
  for (const s of all) { const p = last(s); if (p && (!best || p.t > best.t)) best = p; }
  if (!best) return null;
  const act: StagedAction = [];
  for (const s of all) {
    let k = s.pending.length;
    while (k > 0 && s.pending[k - 1].t === best.t && s.pending[k - 1].body.msg === best.body.msg) k--;
    if (k === s.pending.length) continue;
    act.push({ scan: s.scan, ops: s.pending.slice(k) });
    s.unstage(s.pending.length - k);
  }
  return act;
}

/** Stage an action taken back by unstageNewest again. */
export function restage(byScan: (scan: string) => LogSession | undefined, act: StagedAction) {
  for (const a of act) for (const p of a.ops) byScan(a.scan)?.stage(p.body, p.why, p.t);
}
